import type { AttendanceRecord, AttendanceStatus } from '@/types'

export const compareText = (a: string, b: string): number => {
    return a.localeCompare(b, 'id', { sensitivity: 'base', numeric: true })
}

export const sortByName = <T extends { name: string }>(items: T[]): T[] => {
    return [...items].sort((a, b) => compareText(a.name, b.name))
}

export const initials = (name: string): string => {
    const parts = name.trim().split(/\s+/).filter(Boolean)

    if (parts.length === 0) return '?'
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()

    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
}

export const emptyCounts = (): Record<AttendanceStatus, number> => ({ H: 0, S: 0, I: 0, A: 0 })

export const countStatuses = (records: Pick<AttendanceRecord, 'status'>[]): Record<AttendanceStatus, number> => {
    const counts = emptyCounts()

    records.forEach(record => {
        counts[record.status] += 1
    })

    return counts
}

export const percent = (part: number, total: number): number => {
    if (!total) return 0

    return Math.round((part / total) * 100)
}

export const average = (values: number[]): number | null => {
    if (values.length === 0) return null

    return values.reduce((sum, value) => sum + value, 0) / values.length
}

// Same number style as the original app: a decimal point, optionally without a trailing ".0"
export const formatDecimal = (value: number | null | undefined, digits = 1, trim = false): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—'

    const fixed = value.toFixed(digits)

    return trim && fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed
}

export const uniqueSorted = (values: string[], direction: 'asc' | 'desc' = 'asc'): string[] => {
    const unique = Array.from(new Set(values)).sort()

    return direction === 'desc' ? unique.reverse() : unique
}

export type GradeLevel = {
    label: string
    tone: 'success' | 'primary' | 'warning' | 'danger'
}

export const gradeLevel = (score: number, kkm: number): GradeLevel => {
    if (score < kkm) return { label: 'Perlu bimbingan', tone: 'danger' }
    if (score >= 90) return { label: 'Sangat baik', tone: 'success' }
    if (score >= 80) return { label: 'Baik', tone: 'primary' }

    return { label: 'Cukup', tone: 'warning' }
}

export const createId = (prefix: string): string => {
    return `${prefix}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`
}

export const normalizeSpaces = (value: string): string => value.replace(/\s+/g, ' ').trim()
